import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Receipt, Search, ChevronLeft, ChevronRight, Eye, Undo2, Repeat, RefreshCw,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SaleDetailModal from '../components/orders/SaleDetailModal';
import RefundModal from '../components/orders/RefundModal';
import ExchangeModal from '../components/orders/ExchangeModal';
import usePermissions from '../hooks/usePermissions';
import {
  fetchSales, fetchSale, refundSale, exchangeSale, clearSale,
} from '../redux/slices/saleSlice';
import { fetchProducts } from '../redux/slices/productSlice';

const fmtRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const statusBadge = (s) => {
  const base = 'px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600 ';
  switch (s) {
    case 'PAID': return <span className={`${base}bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300`}>PAID</span>;
    case 'PARTIAL': return <span className={`${base}bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300`}>PARTIAL</span>;
    case 'PENDING': return <span className={`${base}bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300`}>PENDING</span>;
    default: return <span className={`${base}bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>{s || 'N/A'}</span>;
  }
};

const refundBadge = (s) => {
  if (!s || s === 'NONE') return null;
  const base = 'px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600 ';
  if (s === 'FULL') return <span className={`${base}bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300`}>RETURNED</span>;
  return <span className={`${base}bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300`}>PARTIAL RETURN</span>;
};

export default function Orders() {
  const dispatch = useDispatch();
  const { sales, loading, totalPages, currentPage, sale, error } = useSelector((state) => state.sales);
  const { products, loading: productsLoading } = useSelector((state) => state.products);
  const { can } = usePermissions();

  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [modal, setModal] = useState(null); // { mode: 'detail'|'refund'|'exchange', saleId }
  const [actionLoading, setActionLoading] = useState(false);

  const canRefund = can('sales:refund') || can('sales:void');

  useEffect(() => {
    dispatch(fetchSales({ page: 1, limit: 30 }));
    dispatch(fetchProducts({ page: 1, limit: 200 }));
    return () => dispatch(clearSale());
  }, [dispatch]);

  const refreshList = (page = currentPage) => {
    dispatch(fetchSales({ page, limit: 30 }));
  };

  const filteredSales = useMemo(() => {
    let result = sales || [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          (s.transactionId || '').toLowerCase().includes(q) ||
          (s.operator || '').toLowerCase().includes(q) ||
          (s.customerName || '').toLowerCase().includes(q)
      );
    }
    if (paymentFilter !== 'All') {
      result = result.filter((s) => s.paymentStatus === paymentFilter);
    }
    return result;
  }, [sales, searchQuery, paymentFilter]);

  const openModal = (mode, saleId) => {
    setModal({ mode, saleId });
    dispatch(fetchSale(saleId));
  };

  const saleDetail = sale && modal && sale.saleId === modal.saleId ? sale : null;

  const handleRefund = async (payload) => {
    if (!modal) throw new Error('No sale selected');
    setActionLoading(true);
    try {
      const result = await dispatch(refundSale({ id: modal.saleId, ...payload })).unwrap();
      return result;
    } finally {
      setActionLoading(false);
    }
  };

  const handleExchange = async (payload) => {
    if (!modal) throw new Error('No sale selected');
    setActionLoading(true);
    try {
      const result = await dispatch(exchangeSale({ id: modal.saleId, ...payload })).unwrap();
      return result;
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (page) => {
    dispatch(fetchSales({ page, limit: 30 }));
  };

  const onActionComplete = () => {
    setModal(null);
    dispatch(clearSale());
    refreshList();
  };

  const goToSale = (saleId) => {
    if (saleId) {
      openModal('detail', saleId);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <PageHeader
        title="Orders / Sales History"
        description="View past orders, record refunds, and process changes / exchanges."
        actions={
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => refreshList()}>
            Refresh
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by transaction ID, operator, or customer..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="All">All Payments</option>
          <option value="PAID">PAID</option>
          <option value="PARTIAL">PARTIAL</option>
          <option value="PENDING">PENDING</option>
        </select>
      </div>

      {loading && sales.length === 0 ? (
        <div className="py-20 flex items-center justify-center">
          <LoadingSpinner size="lg" message="Loading orders..." />
        </div>
      ) : filteredSales.length === 0 ? (
        <EmptyState icon={Receipt} title="No orders found" description="Sales made from the POS will appear here." />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/40 text-xs text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Order</th>
                  <th className="px-4 py-3 font-medium">Operator</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-center">Payment</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSales.map((s, idx) => {
                  const saleId = s.saleId || s._id;
                  return (
                    <tr
                      key={saleId}
                      className={`border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${idx === 0 ? 'border-t-0' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">{s.transactionId || 'Order'}</p>
                        <p className="text-xs text-gray-400">{s.saleDate} {s.saleTime}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.operator || s.staffId || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.customerName || 'Walk-in'}</td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">{fmtRs(s.totalAmount)}</p>
                        <p className="text-xs text-gray-400">
                          {s.paymentMethod || ''}
                          {Number(s.refundedAmount) > 0 && ` • returned ${fmtRs(s.refundedAmount)}`}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {statusBadge(s.paymentStatus)}
                        {refundBadge(s.refundStatus) && (
                          <div className="mt-1">{refundBadge(s.refundStatus)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {s.isExchangeReplacement ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">Exchange</span>
                        ) : s.status || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button size="sm" variant="ghost" icon={Eye} onClick={() => openModal('detail', saleId)} title="View order">
                            View
                          </Button>
                          {canRefund && !s.isExchangeReplacement && (
                            <>
                              <Button size="sm" variant="ghost" icon={Undo2} onClick={() => openModal('refund', saleId)} title="Refund items">
                                Refund
                              </Button>
                              <Button size="sm" variant="ghost" icon={Repeat} onClick={() => openModal('exchange', saleId)} title="Change / Exchange">
                                Exchange
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(totalPages > 1) && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>
                  Prev
                </Button>
                <Button variant="secondary" size="sm" icon={ChevronRight} iconPosition="right" disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {error && !loading && (
        <p className="text-xs text-red-500 mt-2">{error}</p>
      )}

      {modal?.mode === 'detail' && (
        <SaleDetailModal saleId={modal.saleId} onClose={() => setModal(null)} onViewExchange={goToSale} />
      )}

      {modal?.mode === 'refund' && saleDetail && (
        <RefundModal
          sale={saleDetail.sale || saleDetail}
          items={saleDetail.items || (saleDetail.sale?.items || [])}
          refunds={saleDetail.refunds || []}
          onClose={() => setModal(null)}
          onRefund={async (payload) => {
            await handleRefund(payload);
            onActionComplete();
          }}
        />
      )}

      {modal?.mode === 'exchange' && saleDetail && (
        <ExchangeModal
          sale={saleDetail.sale || saleDetail}
          items={saleDetail.items || (saleDetail.sale?.items || [])}
          refunds={saleDetail.refunds || []}
          products={products}
          productLoading={productsLoading}
          onClose={() => setModal(null)}
          onExchange={async (payload) => {
            await handleExchange(payload);
            onActionComplete();
          }}
        />
      )}

      {actionLoading && (
        <p className="text-xs text-gray-400 mt-2">Processing...</p>
      )}
    </motion.div>
  );
}